import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, CircularProgress } from "@mui/material";
import axios from "../../utils/api";

const UserTicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Set current date as default
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [employeeCount, setEmployeeCount] = useState(0);
  const [noDataMessage, setNoDataMessage] = useState("");
  const [notUpdatedCount, setNotUpdatedCount] = useState(0);
  const [notUpdatedEmployeeIds, setNotUpdatedEmployeeIds] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showAllData, setShowAllData] = useState(false); // Set to false since we're loading with a date
  const [showOverview, setShowOverview] = useState(true); // Always show overview stats
  const navigate = useNavigate();

  // Dashboard stats data
  const [dashboardStats, setDashboardStats] = useState({
    totalEmployees: 0,
    totalTickets: 0,
    activeEmployees: 0,
    completedTasks: 0
  });

  const fetchDashboardStats = async () => {
    try {
      const [ticketsResponse, employeesResponse] = await Promise.all([
        axios.get("/user-tickets"),
        axios.get("/employee-ids")
      ]);

      const allTickets = ticketsResponse.data;
      const allEmployees = employeesResponse.data.data;

      const totalTickets = allTickets.length; // Count total tickets, not entries
      const activeEmployees = new Set(allTickets.map(ticket => ticket.employeeId)).size;
      
      setDashboardStats({
        totalEmployees: allEmployees.length,
        totalTickets: totalTickets,
        activeEmployees: activeEmployees,
        completedTasks: totalTickets // Use total tickets for completed tasks
      });
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    }
  };

  const fetchTickets = async (date) => {
    try {
      setLoading(true);
      setNoDataMessage("");
      setShowAllData(!date);
      
      const response = await axios.get("/user-tickets", {
        params: { date: date || "" },
      });

      if (response.data.length === 0) {
        setNoDataMessage(
          date
            ? "No data available for the selected date."
            : "No data available in the database."
        );
      }

      setTickets(response.data);
    } catch (error) {
      setError("Error fetching tickets. Please try again later.");
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotUpdatedCount = useCallback(
    async (updatedCount) => {
      try {
        const response = await axios.get("/employee-ids");
        const totalEmployeeCount = response.data.data.length;
        setNotUpdatedCount(totalEmployeeCount - updatedCount);

        const updatedEmployeeIds = new Set(
          tickets.map((ticket) => ticket.employeeId)
        );
        const allEmployeeIds = response.data.data.map(
          (employee) => employee.employeeId
        );
        const notUpdatedEmployeeIds = allEmployeeIds.filter(
          (employeeId) => !updatedEmployeeIds.has(employeeId)
        );
        setNotUpdatedEmployeeIds(notUpdatedEmployeeIds);
      } catch (error) {
        console.error("Error fetching total employee count:", error);
      }
    },
    [tickets]
  );

  useEffect(() => {
    fetchDashboardStats();
    // Always fetch tickets for the selected date (including current date on load)
    if (selectedDate) {
      fetchTickets(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (tickets.length === 0) {
      setEmployeeCount(0);
      setNotUpdatedCount(0);
    } else {
      const uniqueEmployeeIds = new Set(
        tickets.map((ticket) => ticket.employeeId)
      );
      setEmployeeCount(uniqueEmployeeIds.size);
      fetchNotUpdatedCount(uniqueEmployeeIds.size);
    }
  }, [tickets, fetchNotUpdatedCount]);

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };
  
  const handleClearDate = () => {
    setSelectedDate("");
    setTickets([]);
    setShowAllData(true);
    setShowOverview(true);
    setNoDataMessage("");
  };

  const handleShowOverview = () => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
    setShowAllData(false);
    setShowOverview(true);
    setNoDataMessage("");
  };
  
  const handlePopupOpen = () => setShowPopup(true);
  const handlePopupClose = () => setShowPopup(false);

  const styles = {
    container: {
      position: "relative",
      minHeight: "100vh",
      overflow: "hidden",
      background:
        "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      padding: "20px",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    header: {
      color: "#2c3e50",
      textAlign: "center",
      marginBottom: "30px",
      position: "relative",
    },
    backButton: {
      position: "absolute",
      top: "24px",
      left: "24px",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
      color: "#4a4a4a",
      zIndex: 10,
    },
    dashboardSection: {
      marginBottom: "40px",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "20px",
      marginBottom: "30px",
    },
    statCard: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
      borderRadius: "15px",
      padding: "25px",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      transition: "transform 0.3s ease",
      "&:hover": {
        transform: "translateY(-5px)",
      },
    },
    statNumber: {
      fontSize: "2.5rem",
      fontWeight: "bold",
      marginBottom: "5px",
    },
    statLabel: {
      fontSize: "1rem",
      opacity: "0.9",
    },
    controlsSection: {
      background: "white",
      borderRadius: "15px",
      padding: "25px",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
      marginBottom: "30px",
    },
    dateControlsContainer: {
      display: "flex",
      alignItems: "center",
      gap: "15px",
      marginBottom: "20px",
      flexWrap: "wrap",
    },
    button: {
      padding: "12px 20px",
      borderRadius: "8px",
      border: "none",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    secondaryButton: {
      backgroundColor: "#f5f5f5",
      color: "#333",
      "&:hover": {
        backgroundColor: "#e0e0e0",
      },
    },
    welcomeSection: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
      borderRadius: "15px",
      padding: "40px",
      textAlign: "center",
      marginBottom: "30px",
    },
    welcomeTitle: {
      fontSize: "2rem",
      marginBottom: "10px",
      fontWeight: "600",
    },
    welcomeSubtitle: {
      fontSize: "1.1rem",
      opacity: "0.9",
    },
    statsContainer: {
      display: "flex",
      gap: "15px",
      flexWrap: "wrap",
    },
    statBox: {
      background: "#ffffff",
      padding: "15px 20px",
      borderRadius: "10px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      color: "#333",
      fontWeight: "500",
      textAlign: "center",
      minWidth: "180px",
    },
    updatedStat: {
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
    },
    notUpdatedStat: {
      backgroundColor: "#ffebee",
      color: "#c62828",
      cursor: "pointer",
    },
    datePicker: {
      padding: "12px 15px",
      borderRadius: "8px",
      border: "2px solid #e1e8ed",
      fontSize: "16px",
      width: "220px",
      transition: "border-color 0.3s ease",
      "&:focus": {
        borderColor: "#1976d2",
        outline: "none",
      },
    },
    messageBox: {
      textAlign: "center",
      margin: "20px 0",
      padding: "20px",
      borderRadius: "10px",
    },
    noDataMessage: {
      backgroundColor: "#e3f2fd",
      color: "#0d47a1",
    },
    errorMessage: {
      backgroundColor: "#ffebee",
      color: "#b71c1c",
    },
    ticketsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      gap: "20px",
    },
    ticketCard: {
      background: "#ffffff",
      padding: "25px",
      borderRadius: "12px",
      boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
      },
    },
    ticketHeader: {
      color: "#1976d2",
      marginBottom: "15px",
      borderBottom: "2px solid #eee",
      paddingBottom: "10px",
      fontSize: "1.2rem",
      fontWeight: "600",
    },
    ticketDate: {
      color: "#666",
      fontSize: "1rem",
      marginBottom: "20px",
      fontWeight: "500",
    },
    entryItem: {
      marginBottom: "20px",
      paddingBottom: "15px",
      borderBottom: "1px dashed #eee",
      "&:last-child": {
        borderBottom: "none",
        marginBottom: "0",
        paddingBottom: "0",
      },
    },
    popup: {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: "#fff",
      padding: "30px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
      borderRadius: "15px",
      zIndex: 1000,
      maxWidth: "450px",
      width: "90%",
      maxHeight: "80vh",
      overflowY: "auto",
    },
    popupTitle: {
      color: "#333",
      marginBottom: "20px",
      fontSize: "1.3rem",
      fontWeight: "600",
    },
    popupList: {
      listStyleType: "none",
      padding: "0",
      margin: "0 0 25px 0",
    },
    popupListItem: {
      padding: "10px 0",
      borderBottom: "1px solid #f0f0f0",
    },
    closeButton: {
      backgroundColor: "#1976d2",
      color: "#fff",
      padding: "10px 20px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      fontWeight: "500",
      "&:hover": {
        backgroundColor: "#1565c0",
      },
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 999,
    },
    loadingOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0,0,0,0.4)",
      zIndex: 2000,
      flexDirection: "column",
      gap: "14px",
    },
    loadingDots: {
      display: "flex",
      gap: "10px",
      alignItems: "center",
      justifyContent: "center",
    },
    dot: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: "#fff",
      opacity: 0.85,
      transform: "scale(0.8)",
      animation: "dotBounce 1s infinite ease-in-out",
    },
    dotDelay1: { animationDelay: "0s" },
    dotDelay2: { animationDelay: "0.15s" },
    dotDelay3: { animationDelay: "0.3s" },
    loadingMessage: { color: "#fff", fontSize: "1.05rem", fontWeight: 600 },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay} role="status" aria-live="polite">
          <CircularProgress size={64} thickness={4} style={{ color: "#ffffff" }} />
          <div style={styles.loadingMessage}>Loading, please wait</div>
          <div style={styles.loadingDots}>
            <div style={{ ...styles.dot, ...styles.dotDelay1 }} />
            <div style={{ ...styles.dot, ...styles.dotDelay2 }} />
            <div style={{ ...styles.dot, ...styles.dotDelay3 }} />
          </div>
        </div>
        <style>{`
          @keyframes dotBounce {
            0%, 80%, 100% { transform: translateY(0) scale(0.8); opacity: 0.6; }
            40% { transform: translateY(-8px) scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.container, textAlign: "center" }}>
        <div style={{ ...styles.messageBox, ...styles.errorMessage }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <IconButton
        onClick={() => navigate(-1)}
        style={styles.backButton}
      >
        <ArrowBackIcon />
      </IconButton>

      <div
        style={{
          textAlign: "center",
          marginBottom: "30px",
        }}
      >
        <h1
          style={{
            margin: 0,
            marginBottom: "6px",
            fontSize: "1.85rem",
            fontWeight: "700",
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Employee Data Dashboard
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.9rem",
            color: "#64748b",
            fontWeight: "400",
          }}
        >
          View tickets and activity by date
        </p>
      </div>

      {/* Welcome Section */}
      {showAllData && (
        <div style={styles.welcomeSection}>
          <h2 style={styles.welcomeTitle}>Welcome to Employee Dashboard</h2>
          <p style={styles.welcomeSubtitle}>
            Select a date to view specific employee data or browse the overview below
          </p>
        </div>
      )}

      {/* Dashboard Stats - Always show when showOverview is true */}
      {showOverview && (
        <div style={styles.dashboardSection}>
          <h3 style={{ color: "#2c3e50", marginBottom: "20px" }}>
            {selectedDate ? `Overview & Data for ${new Date(selectedDate).toLocaleDateString("en-GB")}` : "Overview"}
          </h3>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{dashboardStats.totalEmployees}</div>
              <div style={styles.statLabel}>Total Employees</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{dashboardStats.activeEmployees}</div>
              <div style={styles.statLabel}>Active Employees</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{dashboardStats.totalTickets}</div>
              <div style={styles.statLabel}>Total Tickets</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>{dashboardStats.completedTasks}</div>
              <div style={styles.statLabel}>Completed Tasks</div>
            </div>
          </div>
        </div>
      )}

      {/* Date Selection Controls */}
      <div style={styles.controlsSection}>
        <h3 style={{ color: "#2c3e50", marginBottom: "15px" }}>Filter by Date</h3>
        <div style={styles.dateControlsContainer}>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            style={styles.datePicker}
            placeholder="Select a date"
          />
          {selectedDate && (
            <button
              onClick={handleClearDate}
              style={{ ...styles.button, ...styles.secondaryButton }}
            >
              View Overview
            </button>
          )}
          {!selectedDate && (
            <button
              onClick={handleShowOverview}
              style={{ ...styles.button, ...styles.secondaryButton }}
            >
              View Today's Data
            </button>
          )}
        </div>

        {/* Display Employee Count only when a date is selected */}
        {selectedDate && (
          <div style={styles.statsContainer}>
            <div style={{ ...styles.statBox, ...styles.updatedStat }}>
              <strong>Updated Employees: {employeeCount}</strong>
            </div>
            <div
              onClick={notUpdatedCount > 0 ? handlePopupOpen : undefined}
              style={{
                ...styles.statBox,
                ...styles.notUpdatedStat,
                cursor: notUpdatedCount > 0 ? "pointer" : "default",
                opacity: notUpdatedCount > 0 ? 1 : 0.7,
              }}
            >
              <strong>Not Updated Employees: {notUpdatedCount}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Display No Data Message */}
      {noDataMessage && (
        <div style={{ ...styles.messageBox, ...styles.noDataMessage }}>
          {noDataMessage}
        </div>
      )}

      {/* Display tickets only when date is selected and data exists */}
      {selectedDate && tickets.length > 0 && (
        <div>
          <h3 style={{ color: "#2c3e50", marginBottom: "20px" }}>
            Employee Data for {new Date(selectedDate).toLocaleDateString("en-GB")}
          </h3>
          <div style={{
            ...styles.ticketsGrid,
            background: "linear-gradient(135deg, #f8fafc 60%, #e0e7ff 100%)",
            borderRadius: "18px",
            boxShadow: "0 8px 32px rgba(52, 152, 219, 0.08)",
            padding: "32px 24px",
            marginTop: "10px",
          }}>
            {tickets.map((ticket) => (
              <div key={ticket._id} style={{
                ...styles.ticketCard,
                background: "linear-gradient(135deg, #ffffff 80%, #e3f0ff 100%)",
                border: "1px solid #e0e7ff",
                boxShadow: "0 4px 24px rgba(52, 152, 219, 0.10)",
                transition: "transform 0.3s, box-shadow 0.3s",
                marginBottom: "8px",
                position: "relative",
                overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "8px",
                  background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}></div>
                <h3 style={{
                  ...styles.ticketHeader,
                  color: "#764ba2",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  marginTop: "12px",
                }}>Employee ID: {ticket.employeeId}</h3>
                <p style={{
                  ...styles.ticketDate,
                  color: "#667eea",
                  fontWeight: 600,
                  marginBottom: "12px",
                }}>
                  <strong>Date: {new Date(ticket.date).toLocaleDateString("en-GB")}</strong>
                </p>
                <div style={{ marginTop: "15px" }}>
                  {ticket.entries.map((entry, index) => (
                    <div key={index} style={{
                      ...styles.entryItem,
                      background: index % 2 === 0 ? "#f8f9fa" : "#e0e7ff",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(52, 152, 219, 0.04)",
                      padding: "16px 18px",
                      marginBottom: "12px",
                      border: "none",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 600, color: "#1976d2" }}>
                          <strong>Ticket No:</strong> {entry.ticketNo}
                        </span>
                        <span style={{
                          background: entry.work === "Ai" ? "#e0f7fa" : "#ede7f6",
                          color: entry.work === "Ai" ? "#00796b" : "#5e35b1",
                          padding: "4px 12px",
                          borderRadius: "16px",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          boxShadow: "0 1px 4px rgba(52, 152, 219, 0.07)",
                        }}>
                          {entry.work}
                        </span>
                      </div>
                      <div style={{ marginTop: "10px", color: "#333", fontSize: "1rem" }}>
                        <strong>Hours:</strong> {entry.noOfHours} <br />
                        <strong>Description:</strong> {entry.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Popup */}
      {showPopup && (
        <div style={styles.popup}>
          <div style={styles.popupTitle}>Not Updated Employees</div>
          <ul style={styles.popupList}>
            {notUpdatedEmployeeIds.map((id, index) => (
              <li key={index} style={styles.popupListItem}>{id}</li>
            ))}
          </ul>
          <button
            onClick={handlePopupClose}
            style={styles.closeButton}
          >
            Close
          </button>
        </div>
      )}

      {/* Overlay */}
      {showPopup && (
        <div
          onClick={handlePopupClose}
          style={styles.overlay}
        />
      )}

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1.0);
          }
        }
      `}</style>
    </div>
  );
};

export default UserTicketsList;